/**
 * Zone feature-gating. A service belongs to a zone, and a zone may switch off
 * features it doesn't offer (e.g. no load balancer, no VPC). The console hides
 * the matching nav entries / actions. Ported from the legacy @console
 * `Internal.Opennebula/hooks/useZoneFeatures` — same default-enabled semantics:
 * a feature is OFF only when the zone explicitly sets it to `false`.
 *
 * Source: `GET /services/:id/s10003/zone` (via the @mgmt v3 strangler proxy) →
 * `data.zone` with `features` (a bool map; an empty `{}` means everything enabled).
 */
import { useQuery } from '@tanstack/react-query';
// Served through the s10003 v3 gateway (same proxy + authz as the rest of compute),
// so the console never falls back to the bare /v2/services/:id endpoint.
import { serviceApi, onPath } from './_shared';

/** Per-zone capability flags (all optional; absent = enabled). */
export interface ZoneFeatures {
  load_balancer?: boolean;
  backup?: boolean;
  snapshot?: boolean;
  volume?: boolean;
  reserved_ip?: boolean;
  security_group?: boolean;
  ssh_key?: boolean;
  networking?: boolean;
  marketplace?: boolean;
  virtual_router?: boolean;
  vpc?: boolean;
  elastic_ip?: boolean;
  [key: string]: boolean | undefined;
}

export type ZoneFeatureKey = keyof ZoneFeatures;

export interface ServiceZone {
  id: string | number;
  name: string;
  description?: string;
  type?: string;
  provider_scope?: string;
  features?: ZoneFeatures;
}

interface ZoneEnvelope {
  data?: { zone?: ServiceZone };
}

/** The zone a compute service is provisioned in (null until loaded / if absent). */
export function useServiceZone(serviceId?: string) {
  return useQuery<ServiceZone | null>({
    queryKey: ['on', serviceId, 'zone'],
    enabled: !!serviceId,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await serviceApi.get<ZoneEnvelope>(onPath(serviceId!, 'zone'));
      return res.data?.data?.zone ?? null;
    },
  });
}

export interface ZoneFeatureFlags {
  zone: ServiceZone | null;
  /** True unless the zone explicitly disables `key`. */
  isEnabled: (key?: ZoneFeatureKey) => boolean;
  hasLoadBalancer: boolean;
  hasBackup: boolean;
  hasSnapshot: boolean;
  hasVolume: boolean;
  hasReservedIp: boolean;
  hasSecurityGroup: boolean;
  hasSshKey: boolean;
  hasNetworking: boolean;
  hasMarketplace: boolean;
  hasVirtualRouter: boolean;
  hasVpc: boolean;
  hasElasticIp: boolean;
}

/** Feature flags for a service's zone, defaulting every feature to enabled. */
export function useZoneFeatures(serviceId?: string): ZoneFeatureFlags {
  const { data: zone } = useServiceZone(serviceId);
  const features = zone?.features;
  // Default-enabled: only `false` turns a feature off.
  const isEnabled = (key?: ZoneFeatureKey) => (key ? features?.[key] !== false : true);
  return {
    zone: zone ?? null,
    isEnabled,
    hasLoadBalancer: isEnabled('load_balancer'),
    hasBackup: isEnabled('backup'),
    hasSnapshot: isEnabled('snapshot'),
    hasVolume: isEnabled('volume'),
    hasReservedIp: isEnabled('reserved_ip'),
    hasSecurityGroup: isEnabled('security_group'),
    hasSshKey: isEnabled('ssh_key'),
    hasNetworking: isEnabled('networking'),
    hasMarketplace: isEnabled('marketplace'),
    hasVirtualRouter: isEnabled('virtual_router'),
    hasVpc: isEnabled('vpc'),
    hasElasticIp: isEnabled('elastic_ip'),
  };
}
